import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { performanceApi } from '../../api/performance';
import { employeeApi } from '../../api/hr';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';

export function PerformancePage() {
  const [cycleModalOpen, setCycleModalOpen] = useState(false);
  const [appraisalModalOpen, setAppraisalModalOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const cyclesQuery = useQuery({ queryKey: ['performance-cycles'], queryFn: () => performanceApi.listCycles() });
  const pendingHRQuery = useQuery({ queryKey: ['performance-pending-hr'], queryFn: () => performanceApi.pendingForHR() });
  const employeesQuery = useQuery({ queryKey: ['hr-employees'], queryFn: () => employeeApi.listForHR(), enabled: appraisalModalOpen });

  const cycleForm = useForm<{ name: string; periodStart: string; periodEnd: string }>();
  const appraisalForm = useForm<{ cycleId: string; employeeId: string }>();

  const createCycleMutation = useMutation({
    mutationFn: (values: { name: string; periodStart: string; periodEnd: string }) => performanceApi.createCycle(values),
    onSuccess: () => { showSuccess('Cycle created.'); queryClient.invalidateQueries({ queryKey: ['performance-cycles'] }); setCycleModalOpen(false); cycleForm.reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const createAppraisalMutation = useMutation({
    mutationFn: (values: { cycleId: string; employeeId: string }) => performanceApi.createAppraisal({ cycleId: Number(values.cycleId), employeeId: Number(values.employeeId) }),
    onSuccess: (result) => { showSuccess('Appraisal opened.'); setAppraisalModalOpen(false); appraisalForm.reset(); window.location.href = `/staff/performance/${result.data.id}`; },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <div className="page-header">
        <h1>Performance Management</h1>
        <span>
          <button onClick={() => setCycleModalOpen(true)}>New Cycle</button>{' '}
          <button onClick={() => setAppraisalModalOpen(true)}>Open Appraisal</button>
        </span>
      </div>

      <section className="card">
        <h2>Cycles</h2>
        {cyclesQuery.data && (
          <DataTable
            rows={cyclesQuery.data.data}
            rowKey={(c) => c.id}
            emptyMessage="No cycles yet."
            columns={[
              { header: 'Name', render: (c) => c.name },
              { header: 'Period', render: (c) => `${c.period_start} - ${c.period_end}` },
              { header: 'Status', render: (c) => <StatusBadge status={c.status} /> },
            ]}
          />
        )}
      </section>

      <section className="card">
        <h2>Awaiting HR (Final Rating)</h2>
        {pendingHRQuery.data && (
          <DataTable
            rows={pendingHRQuery.data.data}
            rowKey={(a) => a.id}
            emptyMessage="Nothing pending."
            columns={[
              { header: 'Employee', render: (a) => <Link to={`/staff/performance/${a.id}`}>{a.employee_name}</Link> },
              { header: 'Cycle', render: (a) => a.cycle_name },
            ]}
          />
        )}
      </section>

      {cycleModalOpen && (
        <Modal title="New Performance Cycle" onClose={() => setCycleModalOpen(false)}>
          <form onSubmit={cycleForm.handleSubmit((v) => createCycleMutation.mutate(v))}>
            <label>Name<input {...cycleForm.register('name', { required: true })} /></label>
            <label>Period Start<input type="date" {...cycleForm.register('periodStart', { required: true })} /></label>
            <label>Period End<input type="date" {...cycleForm.register('periodEnd', { required: true })} /></label>
            <div className="form-actions"><button type="submit" disabled={createCycleMutation.isPending}>Create</button></div>
          </form>
        </Modal>
      )}

      {appraisalModalOpen && (
        <Modal title="Open Appraisal" onClose={() => setAppraisalModalOpen(false)}>
          <form onSubmit={appraisalForm.handleSubmit((v) => createAppraisalMutation.mutate(v))}>
            <label>
              Cycle
              <select {...appraisalForm.register('cycleId', { required: true })}>
                <option value="">Select…</option>
                {cyclesQuery.data?.data.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>
              Employee
              <select {...appraisalForm.register('employeeId', { required: true })}>
                <option value="">Select…</option>
                {employeesQuery.data?.data.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.staff_id ?? 'no ID'})</option>)}
              </select>
            </label>
            <div className="form-actions"><button type="submit" disabled={createAppraisalMutation.isPending}>Open</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
